// src/pages/EditEventPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase'; // Adjust path
import { useAuth } from '../context/AuthContext'; // Adjust path
import styles from '../styles/FormPage.module.css'; // Reuse form styles

const EditEventPage = () => {
    const { eventId } = useParams(); // Get event ID from URL parameter
    const { currentUser, userRole } = useAuth();
    const navigate = useNavigate();

    // State for form fields
    const [title, setTitle] = useState('');
    const [eventDate, setEventDate] = useState(''); // Stored as YYYY-MM-DD
    const [description, setDescription] = useState('');
    const [initialData, setInitialData] = useState(null); // To compare changes

    // State for loading and feedback
    const [loading, setLoading] = useState(true); // Start loading true to fetch data
    const [submitting, setSubmitting] = useState(false); // For update submission
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // Role check and redirect
    useEffect(() => {
        // Redirect immediately if role is loaded and not authorized
        if (userRole && userRole !== 'Manager' && userRole !== 'Accounts Manager') {
            console.log("Edit Event: Access denied. Redirecting...");
            navigate('/dashboard');
        }
    }, [userRole, navigate]);

    // Fetch event data on component mount or when eventId changes
    const fetchEventData = useCallback(async () => {
        // Ensure we have an eventId and the user is potentially authorized
        if (!eventId || (userRole && userRole !== 'Manager' && userRole !== 'Accounts Manager')) {
            if (!eventId) setError("Event ID missing.");
             setLoading(false);
            return;
        }

        setLoading(true);
        setError('');
        try {
            const eventDocRef = doc(db, 'events', eventId);
            const docSnap = await getDoc(eventDocRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                // Populate state with fetched data
                setTitle(data.title || '');
                setEventDate(data.date || ''); // Assuming date is stored as YYYY-MM-DD
                setDescription(data.description || '');
                // Store initial data for comparison on submit
                setInitialData({
                    title: data.title || '',
                    date: data.date || '',
                    description: data.description || '',
                });
            } else {
                setError(`Event not found (ID: ${eventId}).`);
                setInitialData(null); // Ensure initialData is null if not found
            }
        } catch (err) {
            console.error("Error fetching event:", err);
            setError("Failed to load event data. Please try again.");
            setInitialData(null);
        } finally {
            setLoading(false);
        }
    }, [eventId, userRole]); // Depend on eventId and userRole

    // Trigger fetch when component mounts or eventId/userRole changes
    useEffect(() => {
        fetchEventData();
    }, [fetchEventData]); // fetchEventData is memoized by useCallback

    // Check if form data has changed
    const hasChanged = () => {
        if (!initialData) return false; // No initial data to compare against
        return (
            title.trim() !== initialData.title ||
            eventDate !== initialData.date ||
            description.trim() !== initialData.description
        );
    };


    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        if (!title.trim() || !eventDate) {
            setError('Event title and date are required.');
            return;
        }

        if (!hasChanged()) {
            setError("No changes detected to save.");
            return;
        }

        setSubmitting(true);

        try {
            const eventDocRef = doc(db, 'events', eventId);
            const updates = {
                title: title.trim(),
                date: eventDate,
                description: description.trim(),
                updatedAt: serverTimestamp(), // Track last update time
                // Optionally track who updated it
                // updatedBy: currentUser.uid,
            };

            await updateDoc(eventDocRef, updates);

            setSuccessMessage('Event updated successfully!');
            // Update initial data to reflect the save
            setInitialData({ title: updates.title, date: updates.date, description: updates.description });
            // Optionally navigate back after a short delay
            setTimeout(() => navigate('/events'), 1500); // Navigate back to manage page

        } catch (err) {
            console.error("Error updating event:", err);
            setError('Failed to update event. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // --- Render Logic ---

    // Show loading indicator while fetching or checking role
    if (loading) {
        return <div className={styles.loadingIndicator}>Loading event data...</div>;
    }
    // Show error if event wasn't found or fetch failed
    if (error && !initialData) {
        return (
            <div className={styles.formContainer}>
                 <h2 className={styles.title}>Edit Event</h2>
                 <p className={styles.errorMessage}>{error}</p>
                 <button onClick={() => navigate('/events')} className={`${styles.formButton} ${styles.cancelButton}`}>Back to Events</button>
            </div>
        );
    }
     // Prevent non-managers from seeing the form (redundant check, but safe)
     if (!currentUser || (userRole && userRole !== 'Manager' && userRole !== 'Accounts Manager')) {
        return <div className={styles.loadingIndicator}>Access Denied.</div>;
     }


    return (
        <div className={styles.formContainer}> {/* Reuse form container style */}
            <h2 className={styles.title}>Edit Event</h2>

            {/* Display feedback messages */}
            {error && !successMessage && <p className={styles.errorMessage}>{error}</p>}
            {successMessage && <p className={styles.successMessage}>{successMessage}</p>}

            <form onSubmit={handleSubmit} className={styles.form}>
                {/* Event Title */}
                <div className={styles.inputGroup}>
                    <label htmlFor="eventTitle">Event Title</label>
                    <input
                        type="text"
                        id="eventTitle"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                        disabled={submitting}
                    />
                </div>

                {/* Event Date */}
                <div className={styles.inputGroup}>
                    <label htmlFor="eventDate">Date</label>
                    <input
                        type="date"
                        id="eventDate"
                        value={eventDate}
                        onChange={(e) => setEventDate(e.target.value)}
                        required
                        disabled={submitting}
                    />
                </div>

                {/* Event Description (Optional) */}
                <div className={styles.inputGroup}>
                    <label htmlFor="eventDescription">Description (Optional)</label>
                    <textarea
                        id="eventDescription"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows="3"
                        disabled={submitting}
                    />
                </div>

                {/* Submit/Cancel Buttons */}
                <div className={styles.buttonGroup}>
                     <button
                        type="button"
                        onClick={() => navigate('/events')} // Navigate back to the list
                        className={`${styles.formButton} ${styles.cancelButton}`}
                        disabled={submitting}
                     >
                         Cancel
                     </button>
                    <button
                        type="submit"
                        className={`${styles.formButton} ${styles.submitButton}`}
                        disabled={submitting || !hasChanged()} // Disable if submitting or no changes made
                    >
                        {submitting ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditEventPage;
